import * as ProgressPrimitive from "@radix-ui/react-progress";
import clsx from "clsx";
import { createPluggable } from "pluggable-react";
import * as React from "react";
import styles from "./Progress.module.scss";
import { withRefProp } from "../../../utils/withRefProp";

export type ProgressSize = "sm" | "md" | "lg";
export type ProgressIntent = "primary" | "secondary" | "success" | "warning" | "destructive" | "muted";

export interface ProgressProps extends React.ComponentPropsWithoutRef<typeof ProgressPrimitive.Root> {
  ref?: React.ComponentPropsWithRef<typeof ProgressPrimitive.Root>["ref"];
  value?: number | null;
  max?: number;
  size?: ProgressSize;
  intent?: ProgressIntent;
  indicatorClassName?: string;
}

export interface ProgressSegmentProps extends React.HTMLAttributes<HTMLDivElement> {
  value: number;
  max?: number;
  intent?: ProgressIntent;
}

const ProgressContext = React.createContext<{ max: number } | null>(null);

const clampProgress = (value: number | null | undefined, max: number) => {
  if (value === null || value === undefined) return null;
  return Math.min(max, Math.max(0, value));
};

const getPercentage = (value: number, max: number) => {
  if (max <= 0) return 0;
  return (Math.min(max, Math.max(0, value)) / max) * 100;
};

const DefaultProgress = withRefProp(
  ({
    className,
    indicatorClassName,
    children,
    value,
    max = 100,
    size = "md",
    intent = "primary",
    ref,
    ...props
  }: ProgressProps) => {
    const resolvedValue = clampProgress(value, max);
    const percentage = resolvedValue === null ? 0 : getPercentage(resolvedValue, max);
    const hasSegments = React.Children.count(children) > 0;

    return (
      <ProgressContext.Provider value={{ max }}>
        <ProgressPrimitive.Root
          ref={ref}
          className={clsx(styles.progress, className)}
          data-deci-progress
          data-size={size}
          data-intent={intent}
          data-indeterminate={resolvedValue === null ? "true" : "false"}
          data-segmented={hasSegments ? "true" : "false"}
          value={resolvedValue}
          max={max}
          {...props}
        >
          {hasSegments ? (
            children
          ) : (
            <ProgressPrimitive.Indicator
              className={clsx(styles.indicator, indicatorClassName)}
              data-deci-progress-indicator
              style={{ transform: `translateX(-${100 - percentage}%)` }}
            />
          )}
        </ProgressPrimitive.Root>
      </ProgressContext.Provider>
    );
  },
);

const ProgressSegment = ({ className, intent = "primary", max, style, value, ...props }: ProgressSegmentProps) => {
  const context = React.useContext(ProgressContext);
  const resolvedMax = max ?? context?.max ?? 100;
  const percentage = getPercentage(value, resolvedMax);
  const segmentStyle = {
    "--progress-segment-width": `${percentage}%`,
    ...style,
  } as React.CSSProperties;

  return (
    <div
      aria-hidden="true"
      className={clsx(styles.segment, className)}
      data-deci-progress-segment
      data-intent={intent}
      data-visible={percentage > 0 ? "true" : "false"}
      style={segmentStyle}
      {...props}
    />
  );
};

const Progress = Object.assign(createPluggable<ProgressProps>("deci.progress", DefaultProgress), {
  Segment: ProgressSegment,
});

export default Progress;
